import { useMemo } from "react";
import type { OperationVariables } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import {
  storageFormatters,
  type GeneralBarDatum,
  type LineData,
  type PieChartConfig,
  type PieInnerTextProps,
  type TotalsAnnotationsValues,
} from "@jana-technology/platform-ui";

import { REFERENCE_LINE_BASE } from "constants/charts";

import {
  TopRiskWellsInterventionDocument,
  TopRiskWellsOperationalDocument,
} from "$/__generated__/graphql";
import {
  appendORIRData,
  appendSystemOverviewTooltipExtras,
  appendTopRiskWellsSubgroups,
} from "./utils";

export const useTopRiskWellsByThreatIntervention = (
  options?: OperationVariables,
) => {
  const topRiskWellsInterventionResult = useQuery(
    TopRiskWellsInterventionDocument,
    options,
  );

  // Used for pie chart inner text for operational system total
  // Remains constant unlike intervention call's operationalSystemTotal which changes based on selection
  const topRiskWellsOperationalResult = useQuery(
    TopRiskWellsOperationalDocument,
    options,
  );

  const memoizedResult = useMemo(() => {
    const riskByThreatsData: Array<GeneralBarDatum> = [];
    const riskByConsequenceData: Array<GeneralBarDatum> = [];
    const riskByOutcomeData: Array<GeneralBarDatum> = [];

    const riskByThreatsDataKeys: Array<string> = [];
    const riskByConsequenceDataKeys: Array<string> = [];
    const riskByOutcomeDataKeys: Array<string> = [];

    const topWellsMedianLines: Array<LineData> = [];
    const topWellsTotals: TotalsAnnotationsValues = [];

    const topWellsPieChartData: Array<PieChartConfig> = [];
    const topWellsPieChartInnerText: Array<PieInnerTextProps> = [];

    let yMaxStackVal;

    if (
      topRiskWellsInterventionResult.data &&
      topRiskWellsOperationalResult.data
    ) {
      const {
        systemMedian,
        selectionMedian,
        interventionSelectionTotal,
        interventionSystemTotal,
        topInterventionTotal,
        total: interventionTotal,
        items,
      } = topRiskWellsInterventionResult.data.topRiskWellsSummaryIntervention;

      const {
        operationalSelectionTotal,
        operationalSystemTotal,
        topOperationalTotal,
        total: operationalTotal,
      } = topRiskWellsOperationalResult.data.topRiskWellsSummaryOperational;

      topWellsPieChartData.push(
        {
          id: "system-risk-breakdown-or",
          name: "Operational Risk",
          data: [
            {
              label: "Top 10 Wells Risk",
              value: topOperationalTotal ?? 0,
            },
            {
              label: "Others in Selection",
              value: operationalSelectionTotal ?? 0,
            },
            {
              label: "Others in System",
              value: operationalSystemTotal ?? 0,
            },
          ],
        },
        {
          id: "system-risk-breakdown-ir",
          name: "Intervention Risk",
          data: [
            {
              label: "Top 10 Wells Risk",
              value: topInterventionTotal ?? 0,
            },
            {
              label: "Others in Selection",
              value: interventionSelectionTotal ?? 0,
            },
            {
              label: "Others in System",
              value: interventionSystemTotal ?? 0,
            },
          ],
        },
      );

      topWellsPieChartInnerText.push(
        {
          label: "OR Total Risk",
          value: storageFormatters.currency(operationalTotal ?? 0),
        },
        {
          label: "IR Total Risk",
          value: storageFormatters.currency(interventionTotal ?? 0),
        },
      );

      topWellsMedianLines.push(
        {
          id: "top-risk-well-selection-median-line",
          name: "Selection Median",
          yVal: selectionMedian ?? 0,
          ...REFERENCE_LINE_BASE,
        },
        {
          id: "top-risk-well-system-median-line",
          name: "System Median",
          yVal: systemMedian ?? 0,
          ...REFERENCE_LINE_BASE,
          strokeDashoffset: "2",
        },
      );

      for (const item of items) {
        const xBand = `Well ${item.group ?? "unknown"}`;

        const riskByThreatDatumOR: GeneralBarDatum = {
          x: xBand,
          x1: "OR",
        };

        const riskByThreatDatumIR: GeneralBarDatum = {
          x: xBand,
          x1: "IR",
        };

        const riskByConsequenceDatumOR: GeneralBarDatum = {
          x: xBand,
          x1: "OR",
        };

        const riskByConsequenceDatumIR: GeneralBarDatum = {
          x: xBand,
          x1: "IR",
        };

        const riskByOutcomeDatumOR: GeneralBarDatum = {
          x: xBand,
          x1: "OR",
        };

        const riskByOutcomeDatumIR: GeneralBarDatum = {
          x: xBand,
          x1: "IR",
        };

        riskByThreatDatumOR.formatter = (value: number) =>
          storageFormatters.currency(value);
        riskByThreatDatumIR.formatter = (value: number) =>
          storageFormatters.currency(value);
        riskByConsequenceDatumOR.formatter = (value: number) =>
          storageFormatters.currency(value);
        riskByConsequenceDatumIR.formatter = (value: number) =>
          storageFormatters.currency(value);
        riskByOutcomeDatumOR.formatter = (value: number) =>
          storageFormatters.currency(value);
        riskByOutcomeDatumIR.formatter = (value: number) =>
          storageFormatters.currency(value);

        appendORIRData(
          item.riskWellsByThreats,
          riskByThreatDatumOR,
          riskByThreatsDataKeys,
          riskByThreatDatumIR,
        );

        appendORIRData(
          item.riskWellsByConsequence,
          riskByConsequenceDatumOR,
          riskByConsequenceDataKeys,
          riskByConsequenceDatumIR,
        );

        appendORIRData(
          item.riskWellsByOutcomeScenario,
          riskByOutcomeDatumOR,
          riskByOutcomeDataKeys,
          riskByOutcomeDatumIR,
        );

        appendTopRiskWellsSubgroups(
          riskByConsequenceDatumOR,
          item.riskWellsByConsequence,
          "operationalTotal",
          "By Category",
        );

        appendTopRiskWellsSubgroups(
          riskByConsequenceDatumIR,
          item.riskWellsByConsequence,
          "interventionTotal",
          "By Category",
        );

        appendTopRiskWellsSubgroups(
          riskByOutcomeDatumOR,
          item.riskWellsByOutcomeScenario,
          "operationalTotal",
          "Release Outcome",
        );

        appendTopRiskWellsSubgroups(
          riskByOutcomeDatumIR,
          item.riskWellsByOutcomeScenario,
          "interventionTotal",
          "Release Outcome",
        );

        appendSystemOverviewTooltipExtras(
          item.operationalTotal ?? 0,
          item.interventionTotal ?? 0,
          riskByThreatDatumOR,
          riskByThreatDatumIR,
        );

        appendSystemOverviewTooltipExtras(
          item.operationalTotal ?? 0,
          item.interventionTotal ?? 0,
          riskByConsequenceDatumOR,
          riskByConsequenceDatumIR,
        );

        appendSystemOverviewTooltipExtras(
          item.operationalTotal ?? 0,
          item.interventionTotal ?? 0,
          riskByOutcomeDatumOR,
          riskByOutcomeDatumIR,
        );

        riskByThreatsData.push(riskByThreatDatumOR, riskByThreatDatumIR);

        riskByConsequenceData.push(
          riskByConsequenceDatumOR,
          riskByConsequenceDatumIR,
        );

        riskByOutcomeData.push(riskByOutcomeDatumOR, riskByOutcomeDatumIR);

        topWellsTotals.push({
          band: xBand,
          total: item.total ?? 0,
        });
      }

      yMaxStackVal = Math.max(
        ...topWellsTotals.map((annotation) => annotation.total),
      );
    }

    return {
      ...topRiskWellsInterventionResult,
      loading:
        topRiskWellsInterventionResult.loading ||
        topRiskWellsOperationalResult.loading,
      topWellsMedianLines,
      riskByThreatsData,
      riskByThreatsDataKeys,
      riskByConsequenceData,
      riskByConsequenceDataKeys,
      riskByOutcomeData,
      riskByOutcomeDataKeys,
      topWellsPieChartData,
      topWellsPieChartInnerText,
      topWellsTotals,
      yMaxStackVal,
    };
  }, [topRiskWellsInterventionResult, topRiskWellsOperationalResult]);

  return memoizedResult;
};
